import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Modal, useToast } from '../../design-system';
import { getFix } from '../../lib/geolocation';
import { snoozeKey, isSnoozed, snooze, SNOOZE_MINUTES_FALLBACK } from '../../lib/dayPromptSnooze';
import { useLiveRefresh, useRefreshNow } from '../../lib/useLiveRefresh';
import './AttendanceDayPrompt.css';

// ─────────────────────────────────────────────────────────────────────────────
// "Where are you working today?" and, at the end of it, "still in the office?"
//
// The two ends of the working day, asked on whatever page the person happens to
// have open. The morning notification can only nudge somebody towards a screen;
// this is the thing that actually collects the answer, which is why one tap
// here has to be the whole interaction.
//
// ⚠️ THE SERVER DECIDES WHETHER TO ASK, NOT THIS COMPONENT. `/api/attendance/
// today` returns a `prompt` object or null, worked out from the firm's
// timezone, its work week, its holiday list and an approved-leave lookup (see
// utils/attendance.js#dayPrompt). Re-deriving any of that here would mean a
// dialog that appears on Diwali, or to somebody whose leave was approved last
// week — and a prompt that is wrong once is a prompt everybody closes without
// reading from then on.
//
// ⚠️ IT DOES NOT BLOCK THE APP. Every state has a way out that is not an
// answer, and the way out is remembered for an hour rather than for ever. An
// attendance dialog somebody cannot dismiss is one they will answer falsely to
// get rid of, which puts a lie in the register — strictly worse than a missing
// row, because nothing downstream can tell it apart from the truth.
//
// ⚠️ MOUNTED IN THE LAYOUTS, NOT ON THE ATTENDANCE PAGE. The person who has
// forgotten to clock in is by definition not on the attendance page; they are
// on Tasks or in Chat. Mounting it there would show the prompt only to the
// people who did not need it.
// ─────────────────────────────────────────────────────────────────────────────

// Re-ask on a long tab. Somebody who leaves the app open all morning must not
// be asked at 09:45 and then never again — but nor should the dialog reappear
// while they are typing. Every ten minutes is frequent enough to catch the
// transition into the evening and rare enough to be ignorable.
const POLL_MS = 10 * 60 * 1000;

const clockTime = (d) => (d
  ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : '—');

// 'HH:MM' for a time input, in the browser's clock. Close enough for a default
// the person is about to confirm or change; the server re-reads it in the
// FIRM's timezone, which is the only reading that lands in the register.
const nowHhmm = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

function AttendanceDayPrompt() {
  const toast = useToast();
  const navigate = useNavigate();
  // ⚠️ THIS DIALOG MOUNTS IN THE LAYOUT, so it shares no parent with the clock
  // widget, the month table or the team board — the `refreshKey` prop those
  // pass among themselves cannot reach it, in either direction. Marking the day
  // from here left the widget two feet away still offering "Clock in" until the
  // page was reloaded; clocking in from the widget left this dialog still
  // asking where they were working. The app-wide revision is the only channel
  // that crosses that boundary, and it already existed for exactly this.
  const revision = useLiveRefresh();
  const bump = useRefreshNow();
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  // The evening's "I finished at…" input, revealed rather than shown: the
  // common answer is "yes, at the time you recorded", and a time field sitting
  // open invites everybody to fiddle with it.
  const [tillOpen, setTillOpen] = useState(false);
  const [till, setTill] = useState(nowHhmm);
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/attendance/today');
      if (!alive.current) return;
      const next = data?.prompt || null;
      // Snoozed, and the hour is not up. Asked again on the next load.
      if (next && isSnoozed(snoozeKey(next.kind, next.dateKey))) { setPrompt(null); return; }
      setPrompt(next);
    } catch {
      // Silent. A firm that has never opened attendance has no settings row and
      // this 404s or empties; either way the correct behaviour is no dialog,
      // and a toast about it on an unrelated page would be pure noise.
      if (alive.current) setPrompt(null);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    load();
    const poll = setInterval(load, POLL_MS);
    // ⚠️ ALSO ON RETURN TO THE TAB. The evening prompt's moment arrives while
    // the laptop is shut; without this, somebody who reopens it at 18:30 waits
    // up to ten minutes to be asked about a day that has already ended.
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      alive.current = false;
      clearInterval(poll);
      window.removeEventListener('focus', onFocus);
    };
  }, [load, revision]);

  // The firm's own snooze length, carried on the prompt. The fallback only
  // covers a server that did not say.
  const snoozeFor = prompt?.snoozeMinutes ?? SNOOZE_MINUTES_FALLBACK;

  const dismiss = () => {
    if (prompt) snooze(snoozeKey(prompt.kind, prompt.dateKey), snoozeFor);
    setPrompt(null);
  };

  // ── Start: they say where they are working, and that clocks them in ───────
  const start = async (workMode) => {
    setBusy(true);
    try {
      // Asked only when the firm has drawn a fence. The answer above decides
      // what the register says; this is the evidence stored beside it, so a
      // firm can still see whether the phone was in the building.
      const location = prompt?.geofenceEnabled ? await getFix() : null;
      const { data } = await apiClient.post('/api/attendance/clock-in', { workMode, location });
      toast.success(
        data.workMode === 'office'
          ? `Marked in at ${data.officeName || 'the office'}`
          : 'Marked in — working remotely'
      );
      setPrompt(null);
      // Everything else showing today's attendance re-reads now, rather than
      // waiting out its own poll or a navigation.
      bump();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not mark your attendance');
    } finally {
      setBusy(false);
    }
  };

  // ⚠️ THE LEAVE FORM, NOT A LEAVE MARK. The firm runs leave through approvers,
  // balances and a quota year; a dialog that quietly set today to "leave" would
  // leave the register and the leave ledger disagreeing, with no application
  // for anybody to approve or refuse. Snoozed on the way out so the dialog is
  // not sitting on top of the form they were just sent to.
  const applyForLeave = () => {
    if (prompt) snooze(snoozeKey(prompt.kind, prompt.dateKey), snoozeFor);
    setPrompt(null);
    navigate('/employee/attendance?apply=leave');
  };

  // ── Evening: still here, or done ──────────────────────────────────────────
  const answer = async (value, workedTill = null) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post('/api/attendance/day-end', {
        answer: value, workedTill,
      });
      toast.success(
        value === 'working'
          ? (data.resumed ? 'Clock restarted from where it stopped' : 'Noted — still clocked in')
          : workedTill ? `Day closed at ${workedTill}` : 'Day closed'
      );
      setPrompt(null);
      bump();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not record that');
    } finally {
      setBusy(false);
    }
  };

  if (!prompt) return null;

  if (prompt.kind === 'start') {
    const late = prompt.minutesLate;
    return (
      <Modal
        isOpen
        onClose={dismiss}
        title="Marking you in for today"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={dismiss} disabled={busy}>Ask me later</Button>
            <Button variant="secondary" onClick={applyForLeave} disabled={busy}>I&apos;m on leave</Button>
          </>
        }
      >
        <p className="adp-lede">
          {late > 30
            ? `The day started at ${prompt.shiftStartTime}. Where are you working?`
            : `Where are you working today? The firm's day starts at ${prompt.shiftStartTime}.`}
        </p>

        <div className="adp-choices">
          <button type="button" className="adp-choice" disabled={busy} onClick={() => start('office')}>
            <span className="adp-choice-icon" aria-hidden="true">🏢</span>
            <span className="adp-choice-body">
              <span className="adp-choice-label">At the office</span>
              <span className="adp-choice-hint">Clocks you in now</span>
            </span>
          </button>

          <button type="button" className="adp-choice" disabled={busy} onClick={() => start('remote')}>
            <span className="adp-choice-icon" aria-hidden="true">🏠</span>
            <span className="adp-choice-body">
              <span className="adp-choice-label">Working from home</span>
              <span className="adp-choice-hint">Clocks you in, recorded as remote</span>
            </span>
          </button>
        </div>

        <p className="adp-foot">
          Either one starts your clock now. Stepping out for lunch or ending the day is the clock
          on your attendance page — this only marks that you have started.
          {' '}Asked again in {snoozeFor} minutes if you skip it.
        </p>
      </Modal>
    );
  }

  // ── kind === 'evening' ─────────────────────────────────────────────────────
  return (
    <Modal
      isOpen
      onClose={dismiss}
      title={prompt.isOpen ? 'Still working?' : `Your clock stopped at ${prompt.cutoffTime}`}
      size="md"
      footer={<Button variant="ghost" onClick={dismiss} disabled={busy}>Ask me later</Button>}
    >
      <p className="adp-lede">
        {prompt.isOpen
          ? `The firm's day ended at ${prompt.cutoffTime} and you are still clocked in.`
          : `You were still clocked in at ${prompt.cutoffTime}, so the day was closed for you at ${clockTime(prompt.closedAt)}.`}
      </p>
      <p className="adp-figure">{prompt.workedLabel} recorded for {prompt.dateKey}</p>

      <div className="adp-choices">
        <button type="button" className="adp-choice" disabled={busy} onClick={() => answer('working')}>
          <span className="adp-choice-icon" aria-hidden="true">💡</span>
          <span className="adp-choice-body">
            <span className="adp-choice-label">Still in the office</span>
            <span className="adp-choice-hint">
              {prompt.isOpen
                ? 'Keeps the clock running — remember to clock out'
                : 'Picks the clock back up from where it stopped — nothing is lost'}
            </span>
          </span>
        </button>

        <button
          type="button"
          className="adp-choice"
          disabled={busy}
          onClick={() => answer('done')}
        >
          <span className="adp-choice-icon" aria-hidden="true">✓</span>
          <span className="adp-choice-body">
            <span className="adp-choice-label">Done for the day</span>
            <span className="adp-choice-hint">
              {prompt.isOpen ? 'Clocks you out now' : `Accepts ${clockTime(prompt.closedAt)} as your finish`}
            </span>
          </span>
        </button>
      </div>

      {/* ⚠️ THE WHOLE REASON THIS DIALOG BEATS A SILENT AUTO-CLOSE. The cut-off
          records what can be defended, not what was worked — the person who
          stayed until eight is the only one who knows that, and until now the
          only route back was a hand-entry form on another screen that nobody
          opened. */}
      {!tillOpen ? (
        <button type="button" className="adp-link" onClick={() => setTillOpen(true)} disabled={busy}>
          I worked until a different time
        </button>
      ) : (
        <div className="adp-till">
          <label htmlFor="adp-till-input" className="adp-till-label">I worked until</label>
          <input
            id="adp-till-input"
            type="time"
            className="ds-input adp-till-input"
            value={till}
            onChange={(e) => setTill(e.target.value)}
          />
          <Button variant="primary" size="sm" loading={busy} onClick={() => answer('done', till)}>
            Save
          </Button>
        </div>
      )}
    </Modal>
  );
}

export default AttendanceDayPrompt;
